import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { createStoresWithStorage } from '../stores/childNavStores';
import { StorageInterface } from '../stores/childNavStores';
import { createRespondentSessionStore, createNamespacedStorage } from '../stores/respondentSessionStore';
import { createContentStore } from '../stores/contentStore';
import type { SecureLoginResponseDto } from '../types/api/tokenAssessment';

// Define the store context type
type StoresType = ReturnType<typeof createStoresWithStorage>;

// Cache the React context on globalThis so that Vite HMR module re-evaluations
// reuse the same context object rather than creating a new one. Without this,
// after any hot reload the StoreProvider hands a value to the OLD context while
// child components read from the NEW context → "useStores must be used within a
// StoreProvider" error at runtime.
const _g = globalThis as any;
if (!_g.__childNavStoreContext) {
  _g.__childNavStoreContext = createContext<StoresType | null>(null);
}
const StoreContext: React.Context<StoresType | null> = _g.__childNavStoreContext;

interface StoreProviderProps {
  children: ReactNode;
  storage: StorageInterface;
  apiService?: any; // ApiService type
}

export const StoreProvider: React.FC<StoreProviderProps> = ({ 
  children, 
  storage, 
  apiService 
}) => {
  // Create stores with the provided storage
  const stores = React.useMemo(() => {
    const storeInstances = createStoresWithStorage(storage);
    
    // Set API service if provided
    if (apiService && storeInstances.setApiService) {
      storeInstances.setApiService(apiService);
    }
    
    return storeInstances;
  }, [storage, apiService]);

  // Initialize app on mount
  React.useEffect(() => {
    console.log('🔧 StoreProvider: Effect triggered, stores available:', !!stores);
    
    if (stores && (stores as any).executeBootSequence) {
      console.log('🔧 StoreProvider: Calling executeBootSequence(startup)...');
      (stores as any).executeBootSequence('startup').catch((error: unknown) => {
        console.error('StoreProvider: Failed to execute startup boot sequence:', error);
      });
    } else if (stores && stores.initializeApp) {
      // Backward compatibility while all callsites migrate to executeBootSequence.
      console.log('🔧 StoreProvider: executeBootSequence unavailable, falling back to initializeApp...');
      stores.initializeApp().catch((error: unknown) => {
        console.error('StoreProvider: Failed to initialize app:', error);
      });
    } else {
      console.log('🔧 StoreProvider: No stores or boot initialization method available');
    }
  }, [stores]);

  return (
    <StoreContext.Provider value={stores}>
      {children}
    </StoreContext.Provider>
  );
};

// Hooks to use the stores
export const useStores = (): StoresType => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStores must be used within a StoreProvider');
  }
  return context;
};

export const useProfileStore = () => {
  const stores = useStores();
  return stores.useProfileStore;
};

export const useUserStore = () => {
  const stores = useStores();
  return stores.useUserStore;
};

export const useMilestonesStore = () => {
  const stores = useStores();
  return stores.useMilestonesStore;
};

export const useConcernsStore = () => {
  const stores = useStores();
  return stores.useConcernsStore;
};

export const useWellVisitsStore = () => {
  const stores = useStores();
  return stores.useWellVisitsStore;
};

export const useActionsStore = () => {
  const stores = useStores();
  return stores.useActionsStore;
};

export const useContentStore = () => {
  const stores = useStores();
  return stores.useContentStore;
};

interface RespondentStoreProviderProps {
  children: ReactNode;
  storage: StorageInterface;
  resolved: SecureLoginResponseDto;
}

/**
 * Provider for token-based (non-SRA) administration sessions (/secure, /remote, /link).
 * Supplies only what AdminView/ReportView actually read from useStores().
 */
export const RespondentStoreProvider: React.FC<RespondentStoreProviderProps> = ({
  children,
  storage,
  resolved,
}) => {
  const stores = useMemo(() => {
    const sessionStorage = createNamespacedStorage(storage, 'secure_session');
    const useUserStore = createRespondentSessionStore(resolved, sessionStorage);
    const useContentStore = createContentStore(storage);

    const notAvailable = (hookName: string) => (): never => {
      throw new Error(`RespondentStoreProvider: ${hookName} is not available for a token-based administration session`);
    };

    return {
      useUserStore,
      useContentStore,
      useMilestonesStore: notAvailable('useMilestonesStore'),
      useConcernsStore: notAvailable('useConcernsStore'),
      useWellVisitsStore: notAvailable('useWellVisitsStore'),
      useActionsStore: notAvailable('useActionsStore'),
      useProfileStore: notAvailable('useProfileStore'),
      setApiService: (apiService: any) => useUserStore.getState().setApiService(apiService),
      executeBootSequence: async () => {},
      initializeApp: async () => {},
    };
  }, [storage, resolved]);

  return (
    <StoreContext.Provider value={stores as unknown as StoresType}>
      {children}
    </StoreContext.Provider>
  );
};